<template>
  <div class="container-fluid">
    <!-- Rewards Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4" style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h2 class="mb-1 fw-bold text-white">LifeSkills Rewards</h2>
              <p class="mb-0 text-white opacity-75">Earn badges and celebrate your achievements!</p>
            </div>
            <div class="text-white" style="font-size: 48px;">
              🏆
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Main Badges Section -->
      <div class="col-lg-8 mb-4">
        <!-- Overall Progress Summary -->
        <div class="modern-card p-4 mb-4" style="background: linear-gradient(135deg, #10b981 0%, #059669 100%);">
          <div class="d-flex justify-content-between align-items-center text-white">
            <div>
              <h4 class="fw-bold mb-1">{{ earnedBadges.length }} of {{ totalBadges }} BADGES EARNED</h4>
              <p class="mb-0">Complete more tasks to unlock new badges!</p>
            </div>
            <div class="text-end">
              <div class="badge-progress" aria-label="Overall badge progress">
                <div class="progress-bar" role="progressbar" :aria-valuenow="badgeProgressPercentage" aria-valuemin="0" aria-valuemax="100" :style="{ '--progress-width': Math.min(100, Math.max(0, badgeProgressPercentage)) + '%' }">
                  <span class="progress-text">{{ earnedBadges.length }} / {{ totalBadges }} EARNED</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Earned Badges -->
        <div class="mb-4">
          <h4 class="fw-bold mb-3">EARNED BADGES</h4>
          <div class="row">
            <div v-for="badge in earnedBadges" :key="badge.id" class="col-md-6 col-lg-4 mb-4">
              <div class="badge-card earned" @click="viewBadgeDetails(badge)">
                <div class="badge-icon-container" :style="{ background: badge.color }">
                  <div class="badge-icon">{{ badge.icon }}</div>
                  <div class="earned-indicator">✓</div>
                </div>
                <div class="badge-content">
                  <h6 class="fw-bold mb-1 badge-title">{{ badge.name }}</h6>
                  <p class="small badge-description mb-2">{{ badge.description }}</p>
                  <div class="badge-date">
                    <small class="text-success">Earned {{ badge.earnedDate || 'Recently' }}</small>
                  </div>
                  <button class="btn btn-sm btn-outline-primary mt-2" @click.stop="celebrateBadge(badge)">
                    <i class="bi bi-star-fill me-1"></i>Celebrate!
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Locked Badges -->
        <div class="mb-4">
          <h4 class="fw-bold mb-3">LOCKED BADGES 
            <span class="badge bg-secondary ms-2">{{ lockedBadges.length }} Remaining</span>
          </h4>
          <div class="row">
            <div v-for="badge in lockedBadges" :key="badge.id" class="col-md-6 col-lg-4 mb-4">
              <div 
                class="badge-card locked" 
                :class="{ 'almost-unlocked': badge.progress >= 80 }"
                @mouseenter="showTooltip($event, badge)"
                @mouseleave="hideTooltip"
                @click="viewBadgeDetails(badge)"
              >
                <div class="badge-icon-container">
                  <div class="lock-icon">🔒</div>
                  <div v-if="badge.progress > 0" class="progress-ring">
                    <div class="progress-circle" :style="{ '--progress': badge.progress + '%' }"></div>
                    <span class="progress-text">{{ badge.progress }}%</span>
                  </div>
                </div>
                <div class="badge-content">
                  <h6 class="fw-bold mb-1 badge-title">{{ badge.name }}</h6>
                  <p class="small badge-description mb-2">{{ badge.requirement }}</p>
                  <div class="progress-details">
                    <div class="progress-bar-mini">
                      <div class="progress-fill-mini" :style="{ width: badge.progress + '%' }"></div>
                    </div>
                    <small class="text-muted">{{ badge.current || 0 }} / {{ badge.target || 5 }} completed</small>
                  </div>
                  <div class="unlock-hint">
                    <small v-if="badge.progress >= 80" class="text-warning">
                      <i class="bi bi-star-fill me-1"></i>Almost there!
                    </small>
                    <small v-else class="text-muted">{{ badge.hint || 'Keep working to unlock!' }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Keep Earning Notice -->
        <div class="modern-card p-4" style="background: linear-gradient(135deg, #7dd3fc 0%, #38bdf8 100%);">
          <div class="d-flex align-items-center text-white">
            <div class="me-3" style="font-size: 32px;">⭐</div>
            <div>
              <h6 class="fw-bold mb-1">⭐ Keep Earning! Check back daily for new challenges.</h6>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="col-lg-4">
        <!-- Badge Details -->
        <div class="modern-card p-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h5 class="fw-bold mb-0">BADGE SPOTLIGHT</h5>
            <button 
              class="btn btn-sm" 
              style="background: #00d4ff; color: white; border-radius: 10px;"
              @click="celebrateFeaturedBadge"
            >
              🎉 Celebrate!
            </button>
          </div>

          <!-- Featured Badge -->
          <div class="featured-badge text-center mb-4">
            <div class="featured-badge-icon">
              {{ featuredBadge.icon }}
            </div>
            <h5 class="fw-bold mt-3">{{ featuredBadge.name }}</h5>
            <p class="text-muted small">{{ featuredBadge.description }}</p>
            
            <!-- Progress Bar -->
            <div class="progress-container mb-3">
              <div class="progress-bar-custom">
                <div class="progress-fill" :style="{ width: featuredBadge.progress + '%' }"></div>
              </div>
              <small class="text-muted">{{ featuredBadge.current }} / {{ featuredBadge.target }} Completed</small>
            </div>

            <button 
              class="btn w-100" 
              :class="showBadgeDetails ? 'btn-secondary' : 'btn-success'"
              style="border-radius: 10px;"
              @click="toggleBadgeDetails"
            >
              {{ showBadgeDetails ? '← HIDE DETAILS' : 'VIEW DETAILS →' }}
            </button>
          </div>

          <!-- Expandable Badge Details Section -->
          <div v-if="showBadgeDetails" class="badge-details-expanded">
            <div class="details-header">
              <h6 class="fw-bold mb-3">
                <i class="bi bi-info-circle me-2"></i>Badge Requirements
              </h6>
            </div>
            
            <!-- Requirements List -->
            <div class="requirement-list mb-3">
              <div class="requirement-item-inline">
                <i class="bi bi-check-circle text-success me-2"></i>
                <span>{{ featuredBadge.requirement }}</span>
              </div>
            </div>

            <!-- Progress Breakdown -->
            <div class="progress-breakdown mb-3">
              <h6 class="fw-bold mb-2">Progress Breakdown</h6>
              <div class="progress-stats">
                <div class="stat-row">
                  <span class="stat-label">Completed:</span>
                  <span class="stat-value text-success">{{ featuredBadge.current }}</span>
                </div>
                <div class="stat-row">
                  <span class="stat-label">Remaining:</span>
                  <span class="stat-value text-warning">{{ featuredBadge.target - featuredBadge.current }}</span>
                </div>
                <div class="stat-row">
                  <span class="stat-label">Progress:</span>
                  <span class="stat-value text-primary">{{ featuredBadge.progress }}%</span>
                </div>
              </div>
            </div>

            <!-- Tips Section -->
            <div class="tips-section-inline">
              <h6 class="fw-bold mb-2">
                <i class="bi bi-lightbulb me-2"></i>Tips to Unlock
              </h6>
              <div class="tips-list-inline">
                <div class="tip-item">
                  <i class="bi bi-arrow-right text-primary me-2"></i>
                  {{ featuredBadge.tip1 || 'Stay consistent with daily activities' }}
                </div>
                <div class="tip-item">
                  <i class="bi bi-arrow-right text-primary me-2"></i>
                  {{ featuredBadge.tip2 || 'Set small daily goals' }}
                </div>
                <div class="tip-item">
                  <i class="bi bi-arrow-right text-primary me-2"></i>
                  {{ featuredBadge.tip3 || 'Track your progress regularly' }}
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="detail-actions mt-3">
              <button 
                class="btn btn-primary btn-sm me-2"
                @click="trackFeaturedProgress"
              >
                <i class="bi bi-target me-1"></i>Track Progress
              </button>
              <button 
                class="btn btn-outline-success btn-sm"
                @click="motivateUser"
              >
                <i class="bi bi-heart me-1"></i>Get Motivated
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    

    <!-- Badge Detail Modal -->
    <div v-if="showBadgeDetail" class="modal-overlay badge-detail-overlay" @click="showBadgeDetail = false">
      <div class="modal-content badge-detail-modal" @click.stop>
        <div class="modal-header badge-detail-header" :style="{ background: selectedBadge.color }">
          <div class="text-center w-100 text-white">
            <div class="badge-detail-icon">{{ selectedBadge.icon }}</div>
            <h4 class="fw-bold mb-1">{{ selectedBadge.name }}</h4>
            <p class="mb-0 opacity-75">{{ selectedBadge.description }}</p>
          </div>
          <button class="btn-close text-white" @click="showBadgeDetail = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="selectedBadge.earned" class="earned-badge-details">
            <div class="achievement-celebration text-center mb-4">
              <div class="celebration-icon">🎉</div>
              <h5 class="text-success">Achievement Unlocked!</h5>
              <p class="text-muted">Earned on {{ selectedBadge.earnedDate || 'Recently' }}</p>
            </div>
            
            <div class="badge-stats">
              <h6 class="fw-bold mb-3">Badge Details</h6>
              <div class="row">
                <div class="col-6">
                  <div class="stat-item">
                    <div class="stat-icon">📅</div>
                    <div>
                      <small class="text-muted">Date Earned</small>
                      <div class="fw-bold">{{ selectedBadge.earnedDate || 'Recently' }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="stat-item">
                    <div class="stat-icon">⭐</div>
                    <div>
                      <small class="text-muted">Difficulty</small>
                      <div class="fw-bold">{{ selectedBadge.difficulty || 'Medium' }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="badge-actions text-center mt-4">
              <button class="btn btn-success me-2" @click="celebrateBadge(selectedBadge)">
                <i class="bi bi-star-fill me-2"></i>Celebrate Again!
              </button>
              <button class="btn btn-outline-primary" @click="shareBadge(selectedBadge)">
                <i class="bi bi-share me-2"></i>Share Achievement
              </button>
            </div>
          </div>
          
          <div v-else class="locked-badge-details">
            <div class="progress-section mb-4">
              <h6 class="fw-bold mb-3">Progress to Unlock</h6>
              <div class="large-progress-bar">
                <div class="progress-fill-large" :style="{ width: selectedBadge.progress + '%' }"></div>
                <span class="progress-label">{{ selectedBadge.progress }}% Complete</span>
              </div>
              <p class="text-center mt-2">{{ selectedBadge.current || 0 }} / {{ selectedBadge.target || 5 }} tasks completed</p>
            </div>
            
            <div class="requirements-section">
              <h6 class="fw-bold mb-3">Requirements</h6>
              <div class="requirement-item">
                <i class="bi bi-check-circle text-success me-2"></i>
                <span>{{ selectedBadge.requirement }}</span>
              </div>
              <div class="tips-section mt-3">
                <h6 class="fw-bold mb-2">Tips to Unlock:</h6>
                <ul class="tips-list">
                  <li>{{ selectedBadge.tip1 || 'Complete daily tasks consistently' }}</li>
                  <li>{{ selectedBadge.tip2 || 'Focus on quality over quantity' }}</li>
                  <li>{{ selectedBadge.tip3 || 'Ask for help when needed' }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showBadgeDetail = false">Close</button>
          <button v-if="!selectedBadge.earned" class="btn btn-primary" @click="trackProgress(selectedBadge)">
            <i class="bi bi-target me-2"></i>Track Progress
          </button>
        </div>
      </div>
    </div>

    <!-- Tooltip -->
    <div v-if="tooltip.show" class="badge-tooltip" :style="tooltip.style">
      <div class="tooltip-content">
        <h6 class="fw-bold mb-2">{{ tooltip.badge.name }}</h6>
        <p class="mb-2">{{ tooltip.badge.detailedRequirement || tooltip.badge.requirement }}</p>
        <div class="tooltip-progress">
          <div class="mini-progress-bar">
            <div class="mini-progress-fill" :style="{ width: tooltip.badge.progress + '%' }"></div>
          </div>
          <small>{{ tooltip.badge.progress }}% complete</small>
        </div>
      </div>
    </div>

    <!-- Confetti Container -->
    <div v-if="showConfetti" class="confetti-container">
      <div v-for="i in 50" :key="i" class="confetti" :style="getConfettiStyle(i)"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RewardsPage',
  data() {
    return {
      showBadgeDetail: false,
      showBadgeDetails: false,
      showConfetti: false,
      selectedBadge: {},
      tooltip: {
        show: false,
        badge: {},
        style: {}
      },
      featuredBadge: {
        id: null,
        name: 'Loading...',
        description: '',
        icon: '🏆',
        requirement: '',
        progress: 0,
        current: 0,
        target: 0
      },
      earnedBadges: [],
      lockedBadges: [],
      availableIcons: ['🏆', '⭐', '🎯', '🎨', '🎵', '🏃', '📚', '🧮', '🔬', '🌟', '💎', '🎪', '🏅', '👑', '🎭', '🚀'],
      availableColors: ['#86efac', '#60a5fa', '#f87171', '#fbbf24', '#a78bfa', '#34d399', '#fb7185', '#38bdf8', '#f472b6', '#10b981']
    }
  },
  computed: {
    totalBadges() {
      return this.earnedBadges.length + this.lockedBadges.length;
    },
    badgeProgressPercentage() {
      const total = this.totalBadges || 1
      return Math.round((this.earnedBadges.length / total) * 100)
    }
  },
  async mounted() {
    await this.loadBadges()
  },
  methods: {
    async loadBadges() {
      try {
        const raw = localStorage.getItem('currentUser')
        const user = raw ? JSON.parse(raw) : null
        const userId = user?.id
        if (!userId) return
        const { getUserBadges } = await import('../services/badgeService.js')
        const data = await getUserBadges(userId)
        // Map backend shape to UI
        const completed = Array.isArray(data?.completed) ? data.completed : []
        const inProgress = Array.isArray(data?.in_progress) ? data.in_progress : []
        const notBegun = Array.isArray(data?.not_begun) ? data.not_begun : []

        this.earnedBadges = completed.map((b) => ({
          id: b.id,
          name: (b.title || '').toUpperCase(),
          description: b.category ? `Completed ${b.category}` : 'Badge earned',
          icon: b.icon || '🏆',
          color: '#86efac',
          earned: true,
          earnedDate: b.earned_date || 'Recently',
          difficulty: b.difficulty || 'Medium'
        }))

        const progressMapped = inProgress.map((b) => ({
          id: b.id,
          name: (b.title || '').toUpperCase(),
          requirement: b.required ? `Complete ${b.required} ${b.category || 'tasks'}` : 'Keep going',
          detailedRequirement: '',
          progress: b.required ? Math.min(100, Math.round((Number(b.progress || 0) / Number(b.required)) * 100)) : 0,
          current: b.progress || 0,
          target: b.required || 0,
          hint: 'Keep working to unlock!',
          tip1: 'Stay consistent', tip2: 'Plan your week', tip3: 'Ask for help'
        }))
        const notBegunMapped = notBegun.map((b) => ({
          id: b.id,
          name: (b.title || '').toUpperCase(),
          requirement: b.required ? `Complete ${b.required} ${b.category || 'tasks'}` : 'Start your journey',
          detailedRequirement: '',
          progress: 0,
          current: 0,
          target: b.required || 0,
          hint: 'Start now!',
          tip1: 'Start small', tip2: 'Make a plan', tip3: 'Track progress'
        }))

        this.lockedBadges = [...progressMapped, ...notBegunMapped]

        // Pick a featured badge (first locked or first earned)
        if (this.lockedBadges.length > 0) {
          const f = this.lockedBadges[0]
          this.featuredBadge = { ...f, icon: f.icon || '🏆', progress: f.progress || 0 }
        } else if (this.earnedBadges.length > 0) {
          const f = this.earnedBadges[0]
          this.featuredBadge = { ...f, progress: 100, current: f.target || f.required || 0, target: f.target || f.required || 0 }
        }
      } catch (e) {
        // Keep demo data if backend unavailable
        console.error('Failed to load badges', e)
      }
    },
    // Badge detail methods
    async viewBadgeDetails(badge) {
      try {
        const raw = localStorage.getItem('currentUser')
        const user = raw ? JSON.parse(raw) : null
        const userId = user?.id
        if (badge?.id && userId) {
          const { getBadgeDetail } = await import('../services/badgeService.js')
          const detail = await getBadgeDetail(badge.id, userId)
          // Map backend detail to modal format
          this.selectedBadge = {
            id: detail.id || badge.id,
            name: (detail.title || badge.name || '').toUpperCase(),
            description: detail.description || badge.description || '',
            icon: detail.icon || badge.icon || '🏆',
            color: badge.color || '#86efac',
            earned: !!detail.earned_date || badge.earned || false,
            earnedDate: detail.earned_date || badge.earnedDate || '',
            difficulty: detail.difficulty || badge.difficulty || 'Medium',
            progress: detail.progress || badge.progress || 0,
            target: detail.required || badge.target || 0,
            current: detail.progress || badge.current || 0,
            requirement: detail.required ? `Complete ${detail.required} ${detail.category || 'tasks'}` : (badge.requirement || ''),
            tip1: badge.tip1 || 'Stay consistent with daily activities',
            tip2: badge.tip2 || 'Set small daily goals',
            tip3: badge.tip3 || 'Track your progress regularly'
          }
        } else {
          this.selectedBadge = badge
        }
      } catch (e) {
        // Fallback to provided badge if live detail fails
        this.selectedBadge = badge
      }
      this.showBadgeDetail = true
    },
    
    // Featured badge methods
    toggleBadgeDetails() {
      this.showBadgeDetails = !this.showBadgeDetails;
    },
    
    celebrateFeaturedBadge() {
      this.celebrateBadge(this.featuredBadge);
    },
    
    trackFeaturedProgress() {
      this.trackProgress(this.featuredBadge);
    },
    
    motivateUser() {
      const motivationalMessages = [
        "🌟 You're doing amazing! Keep up the great work!",
        "🚀 Every step counts towards your goal!",
        "💪 You're stronger than you think!",
        "🎯 Focus on progress, not perfection!",
        "⭐ Believe in yourself - you've got this!"
      ];
      const randomMessage = motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
      this.showFeedback(randomMessage, 'success');
    },
    
    // Confetti celebration methods
    celebrateBadge(badge) {
      this.showConfetti = true;
      this.showCelebrationFeedback(badge);
      
      // Auto-hide confetti after 3 seconds
      setTimeout(() => {
        this.showConfetti = false;
      }, 3000);
    },
    
    getConfettiStyle() {
      const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#fab1a0', '#fd79a8'];
      return {
        '--delay': Math.random() * 3 + 's',
        '--rotation': Math.random() * 360 + 'deg',
        '--x-end': (Math.random() - 0.5) * 100 + 'vw',
        '--color': colors[Math.floor(Math.random() * colors.length)],
        left: Math.random() * 100 + '%',
        animationDelay: Math.random() * 3 + 's'
      };
    },
    
    // Tooltip methods
    showTooltip(event, badge) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.tooltip = {
        show: true,
        badge: badge,
        style: {
          top: rect.top - 10 + 'px',
          left: rect.left + rect.width + 10 + 'px',
          position: 'fixed',
          zIndex: 1000
        }
      };
    },
    
    hideTooltip() {
      this.tooltip.show = false;
    },
    
    // Progress tracking
    trackProgress(badge) {
      this.showProgressFeedback(badge);
      this.showBadgeDetail = false;
    },
    
    // Social features
    shareBadge(badge) {
      this.showShareFeedback(badge);
    },
    
    // Badge creation removed from student Rewards page. Creation is now on Teacher/Parent dashboards.
    
    // Feedback methods
    showCelebrationFeedback(badge) {
      this.showFeedback(`🎉 Celebrating ${badge.name}! Amazing achievement!`, 'success');
    },
    
    showProgressFeedback(badge) {
      this.showFeedback(`📊 Now tracking progress for ${badge.name}. Keep going!`, 'info');
    },
    
    showShareFeedback(badge) {
      this.showFeedback(`📤 ${badge.name} achievement shared successfully!`, 'success');
    },
    
    showBadgeCreationFeedback(badge) {
      this.showFeedback(`✨ Custom badge "${badge.name}" created successfully!`, 'success');
    },
    
    showFeedback(message, type) {
      const alertClass = type === 'success' ? 'alert-success' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 350px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      
      document.body.appendChild(alert);
      setTimeout(() => {
        if (alert.parentNode) {
          alert.parentNode.removeChild(alert);
        }
      }, 4000);
    }
  }
}
</script>

<style scoped>
.badge-progress {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 9999px;
  padding: 6px 10px;
  min-width: 170px;
  position: relative;
  overflow: hidden;
  display: inline-block;
}

.progress-bar {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 9999px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.progress-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, #34d399, #10b981);
  width: var(--progress-width, 33%);
  border-radius: 9999px;
  transition: width 0.5s ease;
  z-index: 1;
}

.progress-text {
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  position: relative;
  z-index: 2;
  background: rgba(0, 0, 0, 0.35);
  padding: 2px 8px;
  border-radius: 9999px;
  letter-spacing: 0.2px;
}

.badge-card {
  background: var(--bg-primary);
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  height: 100%;
  color: var(--text-primary);
}

.badge-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.badge-card.earned {
  border-color: #10b981;
}

.badge-card.locked {
  opacity: 0.6;
  background: var(--bg-secondary);
}

.badge-icon-container {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
}

.badge-card.locked .badge-icon-container {
  background: #9ca3af !important;
}

.badge-icon {
  font-size: 32px;
}

.lock-icon {
  font-size: 24px;
  color: white;
}

.create-badge-btn {
  font-weight: 600;
  transition: all 0.3s ease;
}

.create-badge-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.featured-badge {
  padding: 20px;
  border-radius: 15px;
  background: linear-gradient(135deg, #86efac 0%, #10b981 100%);
  color: white;
}

.featured-badge-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin: 0 auto;
}

.progress-container {
  margin: 15px 0;
}

.progress-bar-custom {
  background: rgba(255, 255, 255, 0.3);
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-fill {
  background: white;
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 15px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  color: var(--text-primary);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
}

.icon-selector {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-top: 10px;
}

.icon-option {
  width: 50px;
  height: 50px;
  border: 2px solid var(--border-color);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: var(--bg-primary);
}

.icon-option:hover {
  border-color: var(--role-primary);
}

.icon-option.active {
  border-color: var(--role-primary);
  background: var(--role-light);
}

.color-selector {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-top: 10px;
}

.color-option {
  width: 50px;
  height: 50px;
  border: 2px solid var(--border-color);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.color-option:hover {
  transform: scale(1.1);
}

.color-option.active {
  border-color: var(--text-primary);
  transform: scale(1.1);
}

/* Enhanced Badge Styles with Fixed Font Colors */
.badge-title, .badge-description {
  color: var(--text-primary) !important;
}

.earned-indicator {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #10b981;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  border: 2px solid white;
}

/* Progress Indicators */
.progress-ring {
  position: absolute;
  bottom: -10px;
  right: -10px;
  width: 40px;
  height: 40px;
}

.progress-circle {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: conic-gradient(#10b981 var(--progress), #e5e7eb var(--progress));
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-text {
  font-size: 10px;
  font-weight: bold;
  color: white;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-bar-mini {
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-fill-mini {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-details {
  margin-top: 10px;
}

.unlock-hint {
  margin-top: 8px;
}

/* Almost Unlocked State */
.almost-unlocked {
  animation: glow 2s ease-in-out infinite alternate;
  border-color: #fbbf24 !important;
}

@keyframes glow {
  from { box-shadow: 0 0 10px rgba(251, 191, 36, 0.5); }
  to { box-shadow: 0 0 20px rgba(251, 191, 36, 0.8); }
}

/* Badge Detail Modal */
.badge-detail-overlay {
  backdrop-filter: blur(5px);
}

.badge-detail-modal {
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.badge-detail-header {
  padding: 30px;
  border-radius: 15px 15px 0 0;
  position: relative;
}

.badge-detail-icon {
  font-size: 64px;
  margin-bottom: 15px;
  filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
}

.achievement-celebration {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  border-radius: 15px;
  padding: 20px;
}

.celebration-icon {
  font-size: 48px;
  margin-bottom: 10px;
}

.stat-item {
  display: flex;
  align-items: center;
  padding: 15px;
  background: var(--bg-secondary);
  border-radius: 10px;
  margin-bottom: 10px;
}

.stat-icon {
  font-size: 24px;
  margin-right: 10px;
}

.large-progress-bar {
  height: 20px;
  background: #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  margin-bottom: 10px;
}

.progress-fill-large {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  border-radius: 10px;
  transition: width 0.3s ease;
}

.progress-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: bold;
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

.requirement-item {
  display: flex;
  align-items: center;
  padding: 10px;
  background: var(--bg-secondary);
  border-radius: 8px;
  margin-bottom: 10px;
}

.tips-list {
  list-style: none;
  padding: 0;
}

.tips-list li {
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);
  position: relative;
  padding-left: 20px;
}

.tips-list li:before {
  content: "💡";
  position: absolute;
  left: 0;
}

.tips-list li:last-child {
  border-bottom: none;
}

/* Tooltip Styles */
.badge-tooltip {
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 15px;
  border-radius: 10px;
  max-width: 250px;
  box-shadow: 0 8px 16px rgba(0,0,0,0.3);
  pointer-events: none;
}

.tooltip-content h6 {
  color: white !important;
  margin-bottom: 8px;
}

.tooltip-content p {
  color: rgba(255,255,255,0.9) !important;
  margin-bottom: 8px;
}

.mini-progress-bar {
  height: 4px;
  background: rgba(255,255,255,0.3);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 5px;
}

.mini-progress-fill {
  height: 100%;
  background: #10b981;
  border-radius: 2px;
  transition: width 0.3s ease;
}

/* Confetti Animation */
.confetti-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999;
}

.confetti {
  position: absolute;
  width: 10px;
  height: 10px;
  background: var(--color);
  animation: confetti-fall 3s linear;
}

@keyframes confetti-fall {
  0% {
    transform: translateY(-100vh) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) translateX(var(--x-end)) rotate(var(--rotation));
    opacity: 0;
  }
}

/* Enhanced Badge Card Hover Effects */
.badge-card.earned:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.badge-card.locked:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

/* Celebrate Button Animation */
.btn:hover {
  transform: translateY(-1px);
}

/* Progress Animation */
.progress-bar, .progress-fill-mini, .progress-fill-large {
  transition: width 0.5s ease-in-out;
}

/* Expandable Badge Details Styles */
.badge-details-expanded {
  background: var(--bg-secondary);
  border-radius: 15px;
  padding: 20px;
  margin-top: 15px;
  border: 2px solid var(--border-color);
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.details-header {
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 10px;
  margin-bottom: 15px;
}

.requirement-item-inline {
  display: flex;
  align-items: center;
  padding: 10px;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.progress-breakdown {
  background: var(--bg-primary);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid var(--border-color);
}

.progress-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-row {
  display: flex;
  justify-content: between;
  align-items: center;
}

.stat-label {
  font-weight: 500;
  color: var(--text-secondary);
  flex: 1;
}

.stat-value {
  font-weight: bold;
  font-size: 16px;
}

.tips-section-inline {
  background: var(--bg-primary);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid var(--border-color);
}

.tips-list-inline {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tip-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
  color: var(--text-primary);
}

.detail-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  padding-top: 15px;
  border-top: 1px solid var(--border-color);
}

.detail-actions .btn {
  flex: 1;
  max-width: 150px;
}

/* Enhanced Button Hover Effects */
.detail-actions .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .badge-detail-modal {
    margin: 10px;
    max-height: 90vh;
  }
  
  .badge-detail-header {
    padding: 20px;
  }
  
  .badge-detail-icon {
    font-size: 48px;
  }
  
  .badge-tooltip {
    max-width: 200px;
    font-size: 12px;
  }
  
  .stat-item {
    padding: 10px;
  }
  
  .tips-list li {
    font-size: 14px;
  }
  
  .badge-details-expanded {
    padding: 15px;
  }
  
  .detail-actions {
    flex-direction: column;
  }
  
  .detail-actions .btn {
    max-width: none;
  }
  
  .progress-stats {
    gap: 6px;
  }
  
  .tip-item {
    font-size: 13px;
  }
}
</style> 